import { appEnvironment } from "../ApplicationContext";
import { AuthErrorResponse, TokenData } from "../types/AuthResponse";

export default class AuthRequest {
    private get _scopes(): string {
        const userInfo = appEnvironment.USER_INFO_SCOPES?.trim() ?? "";
        const data = appEnvironment.DATA_SCOPES?.trim() ?? "";
        if (userInfo || data) {
            return [userInfo, data].filter((part) => part.length > 0).join(" ");
        }
        return "data:read data:write data:create data:search";
    }

    // Read from appEnvironment dynamically to ensure we get the latest values
    private get _baseUrl(): string {
        return appEnvironment.REACT_APP_AUTH_BASEURL;
    }

    private get _profileUrl(): string {
        return appEnvironment.REACT_APP_PROFILE_BASEURL;
    }

    private get _clientId(): string {
        return appEnvironment.REACT_APP_CLIENT_ID;
    }

    private get _callback(): string {
        return appEnvironment.REACT_APP_AUTH_CALLBACK;
    }

    async authorizeUrl(code_challenge: string): Promise<string> {
        const queryParams = new URLSearchParams({
            response_type: "code",
            client_id: this._clientId,
            redirect_uri: `${this._callback}/oauth/callback`,
            scope: this._scopes,
            prompt: "login",
            code_challenge: code_challenge,
            code_challenge_method: "S256",
            state: "login",
        });

        return `${this._baseUrl}/authorize?${queryParams}`;
    }

    async noPromptAuthorizeUrl(code_challenge: string): Promise<string> {
        const queryParams = new URLSearchParams({
            response_type: "code",
            response_mode: "query",
            client_id: this._clientId,
            redirect_uri: `${this._callback}/oauth/callback`,
            scope: this._scopes,
            code_challenge: code_challenge,
            code_challenge_method: "S256",
            prompt: "none",
            state: "verify",
        });

        return `${this._baseUrl}/authorize?${queryParams}`;
    }

    async logoutUrl(): Promise<string> {
        const queryParams = new URLSearchParams({
            post_logout_redirect_uri: `${this._callback}/oauth/logout`,
        });

        return `${this._baseUrl}/logout?${queryParams}`;
    }

    async getToken(code: string, verifier: string): Promise<TokenData> {
        if (!this._baseUrl || !this._clientId || !this._callback) {
            throw {
                error: "configuration_error",
                error_description: "OAuth configuration is not loaded. Please wait for environment to initialize.",
            } as AuthErrorResponse;
        }

        const url = this._baseUrl + "/token";

        const queryParams = new URLSearchParams({
            client_id: this._clientId,
            grant_type: "authorization_code",
            code: code,
            redirect_uri: `${this._callback}/oauth/callback`,
            code_verifier: verifier,
        });

        return this._fetchToken(url, queryParams);
    }

    async refreshToken(refresh_token: string): Promise<TokenData> {
        if (!this._baseUrl || !this._clientId) {
            throw {
                error: "configuration_error",
                error_description: "OAuth configuration is not loaded. Please wait for environment to initialize.",
            } as AuthErrorResponse;
        }

        const url = `${this._baseUrl}/token`;

        const queryParams = new URLSearchParams({
            client_id: this._clientId,
            grant_type: "refresh_token",
            refresh_token: refresh_token,
            scope: this._scopes,
        });

        return this._fetchToken(url, queryParams);
    }

    private async _fetchToken(url: string, queryParams: URLSearchParams): Promise<TokenData> {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "content-type": "application/x-www-form-urlencoded",
                Accept: "application/json",
            },
            body: queryParams.toString(),
        });

        if (!response.ok) {
            throw {
                error: `no_result_${response.status}`,
                error_description: `No result from OAuth due to ${response.statusText}.`,
            } as AuthErrorResponse;
        }

        try {
            const result = (await response.json()) as TokenData;
            result.token_time = Date.now();
            return result;
        } catch (error) {
            throw {
                error: "no_result",
                error_description: "No result from OAuth.",
            } as AuthErrorResponse;
        }
    }
}
